import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  runTransaction,
  Timestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Diagnosis } from '../types/diagnosis';
import type { ChatMessage } from '../types/chat';

/** Max diagnosis-chat messages kept on the doc (mirrors the server cap). */
const MAX_DIAGNOSIS_CHAT_MESSAGES = 50;

/**
 * Append a [user, assistant] message pair to a diagnosis's saved chat.
 *
 * Uses a transaction (read-modify-write) so two open sessions of the same
 * diagnosis don't clobber each other's turns the way a whole-array overwrite
 * would. Caps the stored conversation at MAX_DIAGNOSIS_CHAT_MESSAGES. Only
 * complete pairs are persisted (never a lone user turn), which keeps the
 * restored history valid for the model.
 */
export async function appendDiagnosisChat(
  userId: string,
  plantId: string,
  diagnosisId: string,
  newPair: ChatMessage[],
): Promise<{ success: boolean; error?: string }> {
  try {
    const ref = doc(db, 'users', userId, 'plants', plantId, 'diagnoses', diagnosisId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const prev = (snap.data()?.chat ?? []) as ChatMessage[];
      const next = [...prev, ...newPair].slice(-MAX_DIAGNOSIS_CHAT_MESSAGES);
      tx.update(ref, { chat: next });
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to save diagnosis chat:', error);
    return { success: false, error: 'Could not save conversation.' };
  }
}

/**
 * Get all diagnoses for a plant, ordered by creation date (newest first)
 */
export async function getDiagnoses(
  userId: string,
  plantId: string,
  maxResults?: number
): Promise<Diagnosis[]> {
  const diagnosesRef = collection(db, 'users', userId, 'plants', plantId, 'diagnoses');
  let q = query(diagnosesRef, orderBy('createdAt', 'desc'));

  if (maxResults) {
    q = query(q, limit(maxResults));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Diagnosis[];
}

/**
 * Get a single diagnosis by ID
 */
export async function getDiagnosis(
  userId: string,
  plantId: string,
  diagnosisId: string
): Promise<Diagnosis | null> {
  const diagnosisRef = doc(db, 'users', userId, 'plants', plantId, 'diagnoses', diagnosisId);
  const snapshot = await getDoc(diagnosisRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Diagnosis;
}

/**
 * Subscribe to diagnoses for a plant (real-time updates)
 */
export function subscribeToDiagnoses(
  userId: string,
  plantId: string,
  callback: (diagnoses: Diagnosis[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const diagnosesRef = collection(db, 'users', userId, 'plants', plantId, 'diagnoses');
  const q = query(diagnosesRef, orderBy('createdAt', 'desc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const diagnoses = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Diagnosis[];
      callback(diagnoses);
    },
    (error) => {
      console.error('Diagnosis subscription error:', error);
      onError?.(error);
    }
  );
}

/**
 * Get diagnoses with photos expiring soon (within 7 days)
 */
export async function getExpiringPhotoDiagnoses(
  userId: string
): Promise<{ plantId: string; diagnosis: Diagnosis }[]> {
  // We need to query across all plants, so we'll need to get all plants first
  // and then check their diagnoses. This is a simplified approach.
  // In production, you might want a collection group query if allowed by rules.
  const plantsRef = collection(db, 'users', userId, 'plants');
  const plantsSnapshot = await getDocs(plantsRef);

  const sevenDaysFromNow = Timestamp.fromMillis(Date.now() + 7 * 24 * 60 * 60 * 1000);
  const now = Timestamp.now();
  const expiringDiagnoses: { plantId: string; diagnosis: Diagnosis }[] = [];

  for (const plantDoc of plantsSnapshot.docs) {
    const diagnosesRef = collection(db, 'users', userId, 'plants', plantDoc.id, 'diagnoses');
    const diagnosesSnapshot = await getDocs(diagnosesRef);

    for (const diagDoc of diagnosesSnapshot.docs) {
      const data = diagDoc.data();
      const photoExpiresAt = data.photoExpiresAt as Timestamp;

      // Check if photo is expiring within 7 days and not already deleted
      if (
        !data.photoDeleted &&
        photoExpiresAt.toMillis() <= sevenDaysFromNow.toMillis() &&
        photoExpiresAt.toMillis() > now.toMillis()
      ) {
        expiringDiagnoses.push({
          plantId: plantDoc.id,
          diagnosis: {
            id: diagDoc.id,
            ...data,
          } as Diagnosis,
        });
      }
    }
  }

  return expiringDiagnoses;
}

/**
 * Calculate days until photo expires
 */
export function getDaysUntilExpiration(photoExpiresAt: Timestamp): number {
  const now = Date.now();
  const expiresAtMs = photoExpiresAt.toMillis();
  const diffMs = expiresAtMs - now;
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}
